import React, { useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { IconButton } from 'components/buttons/IconButton';
import './CalendarRange.less';

export const CALENDAR_MIN_WIDTH = 300;

/** Total spread of years to show at once in years picker. */
const YEARS_TOTAL = 15;

export interface DateRange {
  startDate: moment.Moment;
  endDate: moment.Moment;
}

interface ICalendarRangeProps {
  wrapperClass?: string;
  headerClass?: string;
  selectRange: boolean;
  hideClear?: boolean;
  startDate?: moment.Moment;
  endDate?: moment.Moment;
  onRangeChange: (startDate?: moment.Moment, endDate?: moment.Moment) => void;
}

/**
 * Basic Calendar Component.
 *
 * This is currently based around moment.js.
 *
 * This does not manage it's own selected state,
 * it simply renders what's passed in and emits changes. In that sense it
 * is a "controlled component."
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function CalendarRange({
  wrapperClass,
  headerClass,
  startDate,
  endDate,
  selectRange,
  hideClear,
  onRangeChange,
}: ICalendarRangeProps) {
  const todayDate = getMoment()();
  const initialStartDate = startDate && startDate.clone().startOf('day');
  const initialEndDate = initialStartDate && ((selectRange && endDate?.clone()) || initialStartDate).startOf('day');
  const [selectedStartDate, setSelectedStartDate] = useState(initialStartDate);
  const [selectedEndDate, setSelectedEndDate] = useState(initialEndDate);
  const [selectedMonth, setSelectedMonth] = useState((selectedStartDate || todayDate).month());
  const [selectedYear, setSelectedYear] = useState((selectedStartDate || todayDate).year());
  const [startYear, setStartYear] = useState(selectedYear);
  const [choosingMonth, setChoosingMonth] = useState(false);
  const [choosingYear, setChoosingYear] = useState(false);
  const [selectingEndDate, setSelectingEndDate] = useState(false);
  const startDatePropIsoString = initialStartDate && initialStartDate.toISOString();
  const endDatePropIsoString = initialEndDate && initialEndDate.toISOString();
  const [selectingStartDate, setSelectingStartDate] = useState(true);
  const days = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const firstDayOfMonth = getFirstDayOfMonth(
    getMoment()()
      .month(selectedMonth)
      .year(selectedYear)
  );

  useEffect(() => {
    !selectRange && setSelectedEndDate(selectedStartDate);
  }, [selectRange, selectedStartDate]);

  useEffect(() => {
    if (initialStartDate) {
      setSelectedStartDate(initialStartDate);
      setSelectedEndDate(initialEndDate || initialStartDate);
      setChoosingMonth(false);
      setChoosingYear(false);
      if (!selectRange || selectingStartDate || !initialEndDate) {
        setSelectedYear(initialStartDate.year());
        setSelectedMonth(initialStartDate.month());
      } else {
        setSelectedYear(initialEndDate.year());
        setSelectedMonth(initialEndDate.month());
      }
    }
    // run only if iso string is different, not if moment object is different
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDatePropIsoString, endDatePropIsoString]);

  const isSelected = (day: number, month: number) => {
    const date = getMoment()(`${day}/${month + 1}/${selectedYear}`, 'DD/MM/YYYY').startOf('day');
    return date.isSameOrAfter(selectedStartDate) && date.isSameOrBefore(selectedEndDate);
  };

  const getDayButtonClassName = (day: number, month: number) => {
    return classNames('dpc-calendar__day-button', {
      'dpc-calendar__day-button--selected': isSelected(day, month),
      'dpc-calendar__day-button--inactive': month !== selectedMonth,
    });
  };

  const getDayClassName = (day: number, month: number) => {
    const date = getMoment()(`${day}/${month + 1}/${selectedYear}`, 'DD/MM/YYYY').startOf('day');
    return classNames('dpc-calendar__day', {
      'dpc-calendar__day--start': selectRange && date.isSame(selectedStartDate),
      'dpc-calendar__day--end': selectRange && date.isSame(selectedEndDate),
      'dpc-calendar__day--selected': isSelected(day, month),
    });
  };

  const getMonthClass = (active: boolean) =>
    classNames('dpc-calendar__title-month', {
      'dpc-calendar__title-month--active': active,
    });
  const getYearClass = (active: boolean) =>
    classNames('dpc-calendar__title-year', {
      'dpc-calendar__title-year--active': active,
    });

  const getClassName = () => classNames(wrapperClass, 'dpc-calendar', { 'dpc-calendar--select-range': selectRange });

  const getHeaderClassName = () => classNames(headerClass, 'dpc-calendar__title-bar');

  const onDayClick = (day: number, month: number) => {
    let startDateClone = (selectedStartDate || todayDate).clone();
    let endDateClone = (selectedEndDate || todayDate).clone();
    let newDate = startDateClone.clone();
    let year = selectedYear;
    if (month !== selectedMonth) {
      if (month === 12) {
        year++;
        month = 0;
      } else if (month === -1) {
        year--;
        month = 11;
      }
    }
    const updatedDateTime = new Date(
      year,
      month,
      day,
      newDate.get('hour'),
      newDate.get('minute'),
      newDate.get('second')
    );
    newDate = getMoment()(updatedDateTime);
    if (selectRange && newDate.isAfter(endDateClone)) {
      setSelectedEndDate(newDate);
      setSelectingStartDate(false);
      onRangeChange(startDateClone, newDate.clone());
    } else {
      if (selectingEndDate && newDate.isAfter(selectedStartDate)) {
        setSelectedEndDate(newDate);
        setSelectingStartDate(false);
        selectRange && setSelectingEndDate(false);
        onRangeChange(startDateClone, newDate.clone());
      } else {
        setSelectedStartDate(newDate);
        setSelectingStartDate(true);
        selectRange && setSelectingEndDate(true);
        onRangeChange(newDate, endDateClone);
      }
    }
  };

  const onTodayClick = useCallback(() => {
    if (!selectRange) {
      setSelectedStartDate(getMoment()().startOf('day'));
      setSelectedMonth(getMoment()().month());
      setSelectedYear(getMoment()().year());
      setChoosingMonth(false);
      setChoosingYear(false);
      onRangeChange(getMoment()().startOf('day'), getMoment()().startOf('day'));
    }
  }, [selectRange, onRangeChange]);

  const onClearClick = useCallback(() => {
    if (!selectRange) {
      setSelectedStartDate(undefined);
      setChoosingMonth(false);
      setChoosingYear(false);
      onRangeChange(undefined, undefined);
    }
  }, [onRangeChange, selectRange]);

  const onMonthClick = (e: React.MouseEvent) => {
    const month = (e.target as any).id;
    let monthNo = getMoment()
      .months()
      .indexOf(month);
    setSelectedMonth(monthNo);
    setChoosingMonth(false);
  };

  const onYearClick = (e: React.MouseEvent) => {
    const year = (e.target as any).id;
    setSelectedYear(Number(year));
    setStartYear(Number(year));
    setChoosingYear(false);
  };

  const onCalendarScroll = (e: React.UIEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // TODO this isn't firing... but scrolling the month/year is pretty handy for these.
  };
  // get previous month
  let previousMonthDays = getDaysInMonth(
    getMoment()()
      .month(selectedMonth - 1)
      .year(selectedYear)
  );
  let blanks: React.ReactElement[] = [];
  for (let i = 0; i < firstDayOfMonth; i++) {
    const day = previousMonthDays;
    blanks.push(
      <td key={previousMonthDays} className={getDayClassName(previousMonthDays, selectedMonth - 1)}>
        <button
          className={getDayButtonClassName(previousMonthDays, selectedMonth - 1)}
          onClick={() => onDayClick(day, selectedMonth - 1)}
        >
          <span className="dpc-calendar__day-button-text">{previousMonthDays}</span>
        </button>
      </td>
    );
    previousMonthDays--;
  }

  let daysInMonth: React.ReactElement[] = [];
  for (
    let d = 1;
    d <=
    getDaysInMonth(
      getMoment()()
        .month(selectedMonth)
        .year(selectedYear)
    );
    d++
  ) {
    daysInMonth.push(
      <td key={d} className={getDayClassName(d, selectedMonth)}>
        <button
          id={d.toString()}
          className={getDayButtonClassName(d, selectedMonth)}
          onClick={() => onDayClick(d, selectedMonth)}
        >
          <span className="dpc-calendar__day-button-text">{d}</span>
        </button>
      </td>
    );
  }

  var totalSlots = [...blanks.reverse(), ...daysInMonth];
  let rows: JSX.Element[][] = [];
  let cells: JSX.Element[] = [];

  totalSlots.forEach((row, i) => {
    if (i % 7 !== 0) {
      cells.push(row); // if modulus not equal 7 that means not go to next week
    } else {
      rows.push(cells); // when reach next week we contain all td in last week to rows
      cells = []; // empty container
      cells.push(row); // in current loop we still push current row to new container
    }
    if (i === totalSlots.length - 1) {
      if (cells.length < 7) {
        let extraDays = 1;
        for (let i = cells.length; i < 7; i++) {
          const day = extraDays;
          cells.push(
            <td key={extraDays} className={getDayClassName(extraDays, selectedMonth + 1)}>
              <button
                className={getDayButtonClassName(extraDays, selectedMonth + 1)}
                onClick={() => onDayClick(day, selectedMonth + 1)}
              >
                <span className="dpc-calendar__day-button-text">{extraDays}</span>
              </button>
            </td>
          );
          extraDays++;
        }
      }
      // when end loop we add remain date
      rows.push(cells);
    }
  });

  let rowsByWeek = rows.map((d, i) => {
    return <tr key={i}>{d}</tr>;
  });

  return (
    <section className={getClassName()}>
      <h4 className={getHeaderClassName()}>
        {!choosingMonth && (
          <IconButton
            className="dpc-calendar__prev-button"
            iconClassName="dpc-calendar__prev-icon"
            iconSize="small"
            aria-label={choosingYear ? `Previous Year` : `Previous Month`}
            onClick={() => {
              if (!choosingYear) {
                setSelectedMonth(val => {
                  if (val === 0) {
                    setSelectedYear(year => year - 1);
                    return 11;
                  }
                  return val - 1;
                });
                setChoosingMonth(false); // TODO useReducer, this is a bit much
                setChoosingYear(false);
              } else {
                setStartYear(val => val - YEARS_TOTAL);
              }
            }}
          />
        )}
        <div className="dpc-calendar__title">
          <button
            className={getMonthClass(choosingMonth)}
            onClick={() => {
              setChoosingMonth(!choosingMonth);
              setChoosingYear(false);
            }}
          >
            {getMoment()()
              .month(selectedMonth)
              .format('MMMM')}
          </button>
          <button
            className={getYearClass(choosingYear)}
            onClick={() => {
              setChoosingYear(!choosingYear);
              setChoosingMonth(false);
            }}
          >
            {getMoment()()
              .year(selectedYear)
              .format('YYYY')}
          </button>{' '}
        </div>
        {!choosingMonth && (
          <IconButton
            className="dpc-calendar__next-button"
            iconClassName="dpc-calendar__next-icon"
            iconSize="small"
            aria-label={choosingYear ? `Next Year` : `Next Month`}
            onClick={() => {
              if (!choosingYear) {
                setSelectedMonth(val => {
                  if (val === 11) {
                    setSelectedYear(year => year + 1);
                    return 0;
                  }
                  return val + 1;
                });
                setChoosingMonth(false);
                setChoosingYear(false);
              } else {
                setStartYear(val => val + YEARS_TOTAL);
              }
            }}
          />
        )}
      </h4>
      {choosingMonth && (
        <div className="dpc-calendar__month-picker">
          {getMonthOptions().map((month, index) => (
            <button className="dpc-calendar__picker-button" key={month} onScroll={onCalendarScroll}>
              <span
                id={month}
                onClick={onMonthClick}
                className={classNames('dpc-calendar__picker-button-text', {
                  'dpc-calendar__picker-button-text--selected': index === selectedMonth,
                })}
              >
                {month.slice(0, 3)}
              </span>
            </button>
          ))}
        </div>
      )}
      {choosingYear && (
        <div className="dpc-calendar__year-picker">
          {getOptionsYears(getMoment()().year(startYear)).map(year => (
            <button className="dpc-calendar__picker-button" key={year}>
              <span
                id={year.toString()}
                onClick={onYearClick}
                className={classNames('dpc-calendar__picker-button-text', {
                  'dpc-calendar__picker-button-text--selected': year === selectedYear,
                })}
              >
                {year}
              </span>
            </button>
          ))}
        </div>
      )}
      {!choosingMonth && !choosingYear && (
        <table className="dpc-calendar__calendar" onScroll={onCalendarScroll}>
          <thead className="dpc-calendar__heading">
            <tr>
              {days.map((day, index) => (
                <td key={index} className="dpc-calendar__day-abbr">
                  {day}
                </td>
              ))}
            </tr>
          </thead>
          <tbody>{rowsByWeek}</tbody>
        </table>
      )}
      {!selectRange && !choosingMonth && !choosingYear && (
        <div className="dpc-calendar__actions">
          <button className="dpc-calendar__action dpc-calendar__action--today" onClick={onTodayClick}>
            Today
          </button>
          {!hideClear && (
            <button className="dpc-calendar__action dpc-calendar__action--clear" onClick={onClearClick}>
              Clear
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function getMoment() {
  // use window.moment, to work around something funny with this setup and webpack externals,
  // which I don't have time to sort out, right now.
  return (window as any).moment as typeof moment;
}

function getFirstDayOfMonth(date: moment.Moment) {
  let firstDay = getMoment()(date)
    .startOf('month')
    .format('d');
  return (firstDay as unknown) as number; // .d.ts seems incorrect here.
}

function getDaysInMonth(date: moment.Moment) {
  return date.daysInMonth();
}

function getOptionsYears(date: moment.Moment) {
  const year = date.get('year');
  const years: number[] = [];
  for (let i = year; i <= year + YEARS_TOTAL; i++) {
    years.push(i);
  }
  return years;
}

function getMonthOptions() {
  return getMoment().months();
}
